<template>
  <div class="bg-neutral-50 min-h-screen pb-16">
    <!-- 页头 -->
    <section class="bg-white border-b border-neutral-100">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 class="text-3xl md:text-4xl font-bold mb-2">
              <span class="text-primary mr-2">✨</span>提示词 · 技巧分享
            </h1>
            <p class="text-neutral-500">热门优质提示词与技术分享，即取即用，也欢迎分享你的干货</p>
          </div>
          <button
            class="px-6 py-3 rounded-md bg-primary text-white font-medium hover:bg-primary/90 transition-colors shadow-sm whitespace-nowrap"
            @click="openPublish"
          >
            <i class="fa fa-share-square-o mr-2"></i>发布分享
          </button>
        </div>
      </div>
    </section>

    <!-- 筛选区 -->
    <section class="bg-white border-b border-neutral-100 sticky top-16 z-30">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div class="flex flex-wrap items-center gap-3">
          <!-- 类型 -->
          <div class="flex bg-neutral-100 rounded-lg p-1">
            <button
              v-for="t in tabs"
              :key="t.id"
              class="px-4 py-1.5 text-sm rounded-md transition-colors"
              :class="tab === t.id ? 'bg-white text-primary shadow-sm font-medium' : 'text-neutral-500 hover:text-neutral-800'"
              @click="changeTab(t.id)"
            >
              {{ t.title }}
            </button>
          </div>
          <!-- 排序 -->
          <div class="flex bg-neutral-100 rounded-lg p-1">
            <button
              class="px-4 py-1.5 text-sm rounded-md transition-colors"
              :class="sort === 'hot' ? 'bg-white text-primary shadow-sm font-medium' : 'text-neutral-500 hover:text-neutral-800'"
              @click="changeSort('hot')"
            >
              <i class="fa fa-fire mr-1"></i>热门
            </button>
            <button
              class="px-4 py-1.5 text-sm rounded-md transition-colors"
              :class="sort === 'new' ? 'bg-white text-primary shadow-sm font-medium' : 'text-neutral-500 hover:text-neutral-800'"
              @click="changeSort('new')"
            >
              <i class="fa fa-clock-o mr-1"></i>最新
            </button>
          </div>
          <!-- 分类 -->
          <el-select
            v-model="category"
            placeholder="全部分类"
            size="default"
            style="width: 140px"
            @change="loadList(true)"
          >
            <el-option label="全部分类" value="" />
            <el-option
              v-for="c in categories"
              :key="c"
              :label="c"
              :value="c"
            />
          </el-select>
          <!-- 搜索 -->
          <div class="relative flex-1 min-w-[180px] max-w-sm">
            <input
              v-model="searchKey"
              type="text"
              placeholder="搜索提示词 / 技巧..."
              class="w-full bg-neutral-100 rounded-lg py-2 px-4 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              @keyup.enter="loadList(true)"
            />
            <i
              class="fa fa-search absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 cursor-pointer"
              @click="loadList(true)"
            ></i>
          </div>
        </div>
      </div>
    </section>

    <!-- 列表 -->
    <section class="container mx-auto px-4 sm:px-6 lg:px-8 pt-8">
      <div
        v-if="loading && list.length === 0"
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
      >
        <div
          v-for="i in 6"
          :key="i"
          class="bg-white rounded-xl border border-neutral-200 h-44 animate-pulse"
        ></div>
      </div>

      <template v-else>
        <div
          v-if="list.length === 0"
          class="text-center py-20 text-neutral-400"
        >
          <i class="fa fa-inbox text-5xl mb-4"></i>
          <p>暂无内容，快来发布第一条分享吧</p>
        </div>

        <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <div
            v-for="item in list"
            :key="item.id"
            class="bg-white rounded-xl p-5 border border-neutral-200 hover:shadow-md hover:border-primary/40 transition-all cursor-pointer flex flex-col"
            @click="viewItem(item)"
          >
            <div class="flex items-center justify-between mb-3">
              <span
                class="text-xs px-2 py-1 rounded-full"
                :class="item.type === 'skill'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-blue-100 text-blue-700'"
              >
                {{ typeText(item.type) }}
              </span>
              <span class="flex items-center gap-2">
                <span class="text-xs text-neutral-400">{{ item.category }}</span>
                <button
                  @click.stop="toggleMyApp(item.id)"
                  class="w-7 h-7 rounded-full flex items-center justify-center transition-colors"
                  :class="
                    inMyApps(item.id)
                      ? 'text-primary bg-primary/10'
                      : 'text-neutral-400 hover:text-primary bg-neutral-100'
                  "
                  :title="
                    inMyApps(item.id) ? '已加入我的应用，点击移出' : '加入我的应用'
                  "
                >
                  <i
                    class="text-xs"
                    :class="inMyApps(item.id) ? 'fa fa-check' : 'fa fa-plus'"
                  ></i>
                </button>
              </span>
            </div>
            <h3 class="font-semibold text-neutral-900 line-clamp-2 mb-2">
              {{ item.title }}
            </h3>
            <p class="text-sm text-neutral-500 line-clamp-2">
              {{ item.content }}
            </p>
            <div class="flex-1"></div>
            <div
              class="flex items-center justify-between text-xs text-neutral-400 pt-3 mt-3 border-t border-neutral-100"
            >
              <span class="truncate mr-2">{{ item.userName || "匿名用户" }}</span>
              <span class="flex items-center gap-3 whitespace-nowrap">
                <span class="text-red-400">
                  <i class="fa fa-thumbs-o-up mr-1"></i>{{ item.likeCount || 0 }}
                </span>
                <span :class="item.isFav ? 'text-amber-500' : ''">
                  <i
                    class="fa mr-1"
                    :class="item.isFav ? 'fa-heart' : 'fa-heart-o'"
                  ></i>{{ item.favCount || 0 }}
                </span>
                <span>
                  <i class="fa fa-eye mr-1"></i>{{ item.viewCount || 0 }}
                </span>
              </span>
            </div>
          </div>
        </div>

        <!-- 分页 -->
        <div
          v-if="total > pageSize"
          class="flex justify-center mt-10"
        >
          <el-pagination
            layout="prev, pager, next"
            :total="total"
            :page-size="pageSize"
            :current-page="page"
            @current-change="changePage"
          />
        </div>
      </template>
    </section>

    <!-- 查看弹窗 -->
    <el-dialog
      v-model="dialogVisible"
      :title="current ? current.title : ''"
      width="640px"
      top="8vh"
    >
      <div v-if="current">
        <div class="flex items-center flex-wrap gap-3 mb-4 text-sm text-neutral-500">
          <span
            class="px-2 py-0.5 rounded-full text-xs"
            :class="current.type === 'skill'
              ? 'bg-green-100 text-green-700'
              : 'bg-blue-100 text-blue-700'"
          >
            {{ typeText(current.type) }}
          </span>
          <span>{{ current.category }}</span>
          <span>{{ current.userName || "匿名用户" }}</span>
          <span class="ml-auto flex items-center gap-4">
            <span class="text-red-400">
              <i class="fa fa-thumbs-o-up mr-1"></i>{{ current.likeCount || 0 }}
            </span>
            <span :class="current.isFav ? 'text-amber-500' : ''">
              <i
                class="fa mr-1"
                :class="current.isFav ? 'fa-heart' : 'fa-heart-o'"
              ></i>{{ current.favCount || 0 }}
            </span>
            <span>
              <i class="fa fa-eye mr-1"></i>{{ current.viewCount || 0 }}
            </span>
          </span>
        </div>
        <pre
          class="bg-neutral-50 border border-neutral-200 rounded-lg p-4 text-sm text-neutral-800 whitespace-pre-wrap break-words max-h-[50vh] overflow-y-auto"
          >{{ current.content }}</pre
        >
      </div>
      <template #footer>
        <button
          class="px-5 py-2 rounded-md border border-primary text-primary text-sm hover:bg-primary/5 transition-colors mr-3"
          @click="likeAction"
        >
          <i class="fa" :class="current && current.isZan ? 'fa-thumbs-up' : 'fa-thumbs-o-up'"></i>
          {{ current && current.isZan ? "已点赞" : "点赞" }}
        </button>
        <button
          class="px-5 py-2 rounded-md border text-sm transition-colors mr-3"
          :class="
            current && current.isFav
              ? 'border-amber-400 text-amber-500 hover:bg-amber-50'
              : 'border-neutral-300 text-neutral-600 hover:bg-neutral-50'
          "
          @click="favAction"
        >
          <i class="fa" :class="current && current.isFav ? 'fa-heart' : 'fa-heart-o'"></i>
          {{ current && current.isFav ? "已收藏" : "收藏" }}
        </button>
        <button
          class="px-5 py-2 rounded-md bg-primary text-white text-sm hover:bg-primary/90 transition-colors"
          @click="copyContent"
        >
          <i class="fa" :class="copied ? 'fa-check' : 'fa-copy'"></i>
          {{ copied ? "已复制" : "复制内容" }}
        </button>
      </template>
    </el-dialog>

    <!-- 发布弹窗 -->
    <el-dialog
      v-model="publishVisible"
      title="发布提示词 / 技巧分享"
      width="600px"
      top="8vh"
      :close-on-click-modal="false"
    >
      <el-form label-position="top">
        <el-form-item label="分享类型" required>
          <el-radio-group v-model="form.type">
            <el-radio value="prompt">✨ 提示词</el-radio>
            <el-radio value="skill">💡 技能技巧</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="分类" required>
          <el-select v-model="form.category" style="width: 100%">
            <el-option
              v-for="c in categories"
              :key="c"
              :label="c"
              :value="c"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="标题" required>
          <el-input
            v-model="form.title"
            maxlength="100"
            show-word-limit
            placeholder="一句话说清这条提示词 / 技巧的用途"
          />
        </el-form-item>
        <el-form-item label="分享内容" required>
          <el-input
            v-model="form.content"
            type="textarea"
            :rows="10"
            maxlength="5000"
            show-word-limit
            placeholder="粘贴完整的提示词内容或技巧说明，大家可以直接复制使用"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <button
          class="px-5 py-2 rounded-md border border-neutral-300 text-neutral-600 text-sm hover:bg-neutral-50 transition-colors mr-3"
          @click="publishVisible = false"
        >
          取消
        </button>
        <button
          class="px-5 py-2 rounded-md bg-primary text-white text-sm hover:bg-primary/90 transition-colors disabled:opacity-50"
          :disabled="submitting"
          @click="submit"
        >
          {{ submitting ? "提交中..." : "提交分享" }}
        </button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage } from "element-plus";
import { requireLogin } from "../utils/authGuard";
import myAppsMixin from "../utils/myApps";

const CATEGORIES = [
  "教学备课",
  "课件制作",
  "班级管理",
  "行政办公",
  "创意写作",
  "编程开发",
  "其他",
];

export default {
  name: "PromptPage",
  mixins: [myAppsMixin("prompt")],
  data() {
    return {
      tabs: [
        { id: "all", title: "全部" },
        { id: "prompt", title: "提示词" },
        { id: "skill", title: "技巧分享" },
      ],
      categories: CATEGORIES,
      tab: "all",
      sort: "hot",
      category: "",
      searchKey: "",
      list: [],
      page: 1,
      pageSize: 12,
      total: 0,
      loading: false,

      dialogVisible: false,
      current: null,
      copied: false,

      publishVisible: false,
      submitting: false,
      form: { type: "prompt", category: "其他", title: "", content: "" },
    };
  },
  computed: {
    isLogin() {
      const user = this.$store.state.user;
      return user && user.uid;
    },
  },
  created() {
    this.loadList(true);
  },
  methods: {
    typeText(type) {
      return type === "skill" ? "技巧分享" : "提示词";
    },
    changeTab(t) {
      if (this.tab === t) return;
      this.tab = t;
      this.loadList(true);
    },
    changeSort(s) {
      if (this.sort === s) return;
      this.sort = s;
      this.loadList(true);
    },
    changePage(p) {
      this.page = p;
      this.loadList(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    async loadList(reset) {
      if (this.loading) return;
      if (reset) this.page = 1;
      this.loading = true;
      try {
        const data = await this.$aesEncrypt({
          page: this.page,
          pageSize: this.pageSize,
          searchKey: this.searchKey,
          type: this.tab === "all" ? "" : this.tab,
          category: this.category,
          sort: this.sort === "hot" ? "hot" : "",
        });
        const res = await this.$axios({
          url: "prompt/list",
          method: "POST",
          data: { data },
        });
        if (res.code === 200) {
          this.list = res.data.list || [];
          this.total = res.data.total || 0;
        }
      } catch (e) {
        console.error("获取提示词列表失败:", e);
      } finally {
        this.loading = false;
      }
    },
    async viewItem(item) {
      this.current = item;
      this.copied = false;
      this.dialogVisible = true;
      // 拉取详情（浏览量+1，登录用户附带点赞状态）
      try {
        const res = await this.$axios({ url: "prompt/detail/" + item.id });
        if (res.code === 200 && res.data && res.data.info) {
          this.current = res.data.info;
        }
      } catch (e) {
        /* 静默失败，展示列表数据 */
      }
    },
    likeAction() {
      if (!this.current) return;
      if (!requireLogin(this, "点赞")) return;
      const id = this.current.id;
      const unZan = this.current.isZan;
      this.current.isZan = !unZan;
      this.current.likeCount = (this.current.likeCount || 0) + (unZan ? -1 : 1);
      this.$axios({
        url: "prompt/like/" + id,
        method: unZan ? "DELETE" : "GET",
      })
        .then((res) => {
          if (res.code === 200 && res.data && res.data.likeCount !== undefined) {
            this.current.likeCount = res.data.likeCount;
            // 同步列表中的计数
            const inList = this.list.find((x) => x.id === id);
            if (inList) {
              inList.likeCount = res.data.likeCount;
              inList.isZan = !unZan;
            }
          } else {
            ElMessage.error(res.msg || "操作失败");
            this.current.isZan = unZan;
            this.current.likeCount =
              (this.current.likeCount || 0) + (unZan ? 1 : -1);
          }
        })
        .catch(() => {
          ElMessage.error("操作失败，请稍后重试");
          this.current.isZan = unZan;
          this.current.likeCount = (this.current.likeCount || 0) + (unZan ? 1 : -1);
        });
    },
    // 收藏 / 取消收藏
    favAction() {
      if (!this.current) return;
      if (!requireLogin(this, "收藏内容")) return;
      const id = this.current.id;
      const unFav = this.current.isFav;
      this.current.isFav = !unFav;
      this.current.favCount = (this.current.favCount || 0) + (unFav ? -1 : 1);
      this.$axios({
        url: "prompt/fav/" + id,
        method: unFav ? "DELETE" : "GET",
      })
        .then((res) => {
          if (res.code === 200 && res.data && res.data.favCount !== undefined) {
            this.current.favCount = res.data.favCount;
            const inList = this.list.find((x) => x.id === id);
            if (inList) {
              inList.favCount = res.data.favCount;
              inList.isFav = !unFav;
            }
            ElMessage.success(unFav ? "已取消收藏" : "收藏成功");
          } else {
            ElMessage.error(res.msg || "操作失败");
            this.current.isFav = unFav;
            this.current.favCount = (this.current.favCount || 0) + (unFav ? 1 : -1);
          }
        })
        .catch(() => {
          ElMessage.error("操作失败，请稍后重试");
          this.current.isFav = unFav;
          this.current.favCount = (this.current.favCount || 0) + (unFav ? 1 : -1);
        });
    },
    copyContent() {
      if (!this.current) return;
      // 访客模式：复制内容属于获取资源，需登录
      if (!requireLogin(this, "复制提示词内容")) return;
      const text = this.current.content || "";
      const done = () => {
        this.copied = true;
        ElMessage.success("已复制到剪贴板");
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(text)
          .then(done)
          .catch(() => this.fallbackCopy(text, done));
      } else {
        this.fallbackCopy(text, done);
      }
    },
    fallbackCopy(text, done) {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.display = "none";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        done();
      } catch (e) {
        ElMessage.error("复制失败，请手动选择复制");
      }
      document.body.removeChild(ta);
    },
    openPublish() {
      if (!requireLogin(this, "发布分享")) return;
      this.form = { type: "prompt", category: "其他", title: "", content: "" };
      this.publishVisible = true;
    },
    async submit() {
      if (!requireLogin(this, "发布分享")) return;
      if (!this.form.title.trim()) {
        ElMessage.warning("请填写标题");
        return;
      }
      if (!this.form.content.trim()) {
        ElMessage.warning("请填写分享内容");
        return;
      }
      this.submitting = true;
      try {
        const data = await this.$aesEncrypt({
          title: this.form.title.trim(),
          type: this.form.type,
          category: this.form.category,
          content: this.form.content.trim(),
        });
        const res = await this.$axios({
          url: "prompt/submit",
          method: "POST",
          data: { data },
        });
        if (res.code === 200) {
          this.publishVisible = false;
          ElMessage.success(
            (res.data && res.data.msg) || "分享已提交，审核通过后将对大家可见"
          );
        } else {
          ElMessage.error(res.msg || "提交失败");
        }
      } catch (e) {
        ElMessage.error("提交失败，请检查网络");
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
